import { GroupedTableGroup } from "./GroupedTable";

// Sorting types
type SortDirection = "asc" | "desc" | null;

export interface SortState<TSource extends object> {
  column: keyof TSource | null;
  direction: SortDirection;
}

/**
 * Detect the data type of a column for proper sorting (internal utility)
 */
function detectColumnDataType<TSource extends object, TGroupMeta = undefined>(
  data: GroupedTableGroup<TSource, TGroupMeta>[],
  columnKey: keyof TSource,
): "string" | "number" | "date" | "boolean" {
  const allItems = data.flatMap((group) => group.items);
  const values = allItems
    .map((item) => item[columnKey])
    .filter((val) => val != null && val !== "");

  if (values.length === 0) return "string";

  const sampleValue = values[0];

  // Check for date (either Date object or date-like string)
  if (sampleValue instanceof Date) return "date";
  if (typeof sampleValue === "string" && !isNaN(Date.parse(sampleValue))) {
    // Check if it looks like a date string (YYYY-MM-DD, ISO format, etc.)
    const dateRegex =
      /^\d{4}-\d{2}-\d{2}|\d{2}\/\d{2}\/\d{4}|\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
    if (dateRegex.test(sampleValue)) return "date";
  }

  // Check for boolean
  if (typeof sampleValue === "boolean") return "boolean";

  // Check for number
  if (typeof sampleValue === "number") return "number";
  if (typeof sampleValue === "string" && !isNaN(Number(sampleValue))) {
    return "number";
  }

  return "string";
}

/**
 * Determine if a column should be sortable
 */
export function isSortableColumn<
  TSource extends object,
  TGroupMeta = undefined,
>(
  columnKey: keyof TSource,
  data: GroupedTableGroup<TSource, TGroupMeta>[],
): boolean {
  // Skip action and checkbox columns
  const nonSortablePatterns = ["actions", "action", "checkbox"];
  if (
    nonSortablePatterns.some((pattern) =>
      String(columnKey).toLowerCase().includes(pattern),
    )
  ) {
    return false;
  }

  // Check if column has data to sort
  const allItems = data.flatMap((group) => group.items);
  const values = allItems
    .map((item) => item[columnKey])
    .filter((val) => val != null && val !== "");

  return values.length > 0;
}

/**
 * Create a comparison function for sorting items by a specific column
 */
function createSortComparator<TSource extends object>(
  columnKey: keyof TSource,
  dataType: "string" | "number" | "date" | "boolean",
  direction: "asc" | "desc",
) {
  return (a: TSource, b: TSource): number => {
    const aVal = a[columnKey];
    const bVal = b[columnKey];

    // Handle null/undefined values
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;

    let comparison = 0;

    switch (dataType) {
      case "number":
        comparison = Number(aVal) - Number(bVal);
        break;
      case "date": {
        const dateA = aVal instanceof Date ? aVal : new Date(String(aVal));
        const dateB = bVal instanceof Date ? bVal : new Date(String(bVal));
        comparison = dateA.getTime() - dateB.getTime();
        break;
      }
      case "boolean":
        comparison = aVal === bVal ? 0 : aVal === true ? 1 : -1;
        break;
      default: // string
        comparison = String(aVal).localeCompare(String(bVal));
    }

    return direction === "asc" ? comparison : -comparison;
  };
}

/**
 * Sort items within groups while maintaining group structure.
 * When flattenGroups is true, combines all items into a single sorted group.
 */
export function sortGroupedData<TSource extends object, TGroupMeta = undefined>(
  data: GroupedTableGroup<TSource, TGroupMeta>[],
  sortState: SortState<TSource>,
  flattenGroups = false,
): GroupedTableGroup<TSource, TGroupMeta>[] {
  if (!sortState.column || !sortState.direction) {
    return data;
  }

  const dataType = detectColumnDataType(data, sortState.column);
  const compareFn = createSortComparator(
    sortState.column,
    dataType,
    sortState.direction,
  );

  if (flattenGroups) {
    // Combine all items from all groups and sort them as one list
    const allItems = data.flatMap((group) => group.items);
    const sortedItems = [...allItems].sort(compareFn);

    // Return a single virtual group with all sorted items
    return [
      {
        groupId: `__temp_flattened_${sortState.direction}__`,
        // Empty label for flattened view
        label: "",
        items: sortedItems,
        // Disable drag sorting when flattened
        sortable: false,
      },
    ];
  }

  return data.map((group) => ({
    ...group,
    items: [...group.items].sort(compareFn),
  }));
}
